import { useCallback, useEffect, useState } from 'react';
import api from 'services/api';
import { listarPresentesPublicos, obterListaPublica } from 'services/listas';
import { ListaResumoPresente, Presente } from './PresentePage.types';

interface GerarPixPayload {
  nomePresenteador: string;
  mensagem: string;
}

export const usePresentePageRules = (slugLista?: string, presenteId?: string) => {
  const [presente, setPresente] = useState<Presente | null>(null);
  const [lista, setLista] = useState<ListaResumoPresente | null>(null);
  const [nomePresenteador, setNomePresenteador] = useState('');
  const [mensagem, setMensagem] = useState('');
  const [loading, setLoading] = useState(true);
  const [processando, setProcessando] = useState(false);
  const [error, setError] = useState('');

  const carregarDados = useCallback(async () => {
    if (!slugLista || !presenteId) {
      setError('Presente não encontrado');
      setLoading(false);
      return;
    }

    try {
      const listaResponse = await obterListaPublica(slugLista);
      setLista(listaResponse.lista as ListaResumoPresente);

      const presentesResponse = await listarPresentesPublicos(slugLista);
      const presenteEncontrado = presentesResponse.presentes.find((p) => p.id === presenteId) as
        | Presente
        | undefined;

      if (!presenteEncontrado) {
        setError('Presente não encontrado');
        return;
      }

      if (presenteEncontrado.presenteado) {
        setError('Este presente já foi escolhido por outra pessoa');
        return;
      }

      setPresente(presenteEncontrado);
      setError('');
    } catch (err) {
      setError('Erro ao carregar dados do presente');
    } finally {
      setLoading(false);
    }
  }, [presenteId, slugLista]);

  useEffect(() => {
    void carregarDados();
  }, [carregarDados]);

  const gerarPix = async (): Promise<string | null> => {
    if (!slugLista || !presenteId) {
      setError('Presente não encontrado');
      return null;
    }

    if (!nomePresenteador.trim()) {
      setError('Preencha seu nome');
      return null;
    }

    setProcessando(true);
    setError('');

    try {
      const payload: GerarPixPayload = {
        nomePresenteador: nomePresenteador.trim(),
        mensagem: mensagem.trim() || `Presente para ${lista?.tituloLista}`,
      };

      const response = await api.post(`/webhook/gerar-pix/${slugLista}/${presenteId}`, payload);
      return response.data.transacao.id as string;
    } catch (err: any) {
      if (err.response?.status === 400) {
        setError(err.response.data.error || 'Dados inválidos');
      } else if (err.response?.status === 404) {
        setError('Presente não encontrado ou já foi presenteado');
      } else {
        setError('Erro ao gerar PIX. Tente novamente.');
      }
      return null;
    } finally {
      setProcessando(false);
    }
  };

  const formatarValor = (valor: number) =>
    valor.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    });

  return {
    presente,
    lista,
    nomePresenteador,
    setNomePresenteador,
    mensagem,
    setMensagem,
    loading,
    processando,
    error,
    setError,
    carregarDados,
    gerarPix,
    formatarValor,
  };
};

export type UsePresentePageRulesReturn = ReturnType<typeof usePresentePageRules>;
